import * as React from 'react';
import {
    ComponentType,
    isValidElement,
    MouseEvent,
    ReactElement,
    useCallback,
    useMemo,
} from 'react';
import PropTypes from 'prop-types';
import {
    CreateButton,
    CreateButtonProps,
    ComponentPropType,
    Title,
    useCreatePath,
    useResourceContext,
} from 'react-admin';

import { Route, Routes, useMatch, useNavigate } from 'react-router-dom';
import { Card, CardContent, styled, SxProps } from '@mui/material';
import { EventDataNode } from 'rc-tree/lib/interface';

import { Tree, TreeProps } from './Tree';
import { UNSAVED_NEW_NODE } from './constants';
import { TreeContextProvider, useTreeController } from './controllers';
import DefaultNodeActions, { NodeActionsProps } from './NodeActions';
import { TreeRecord } from './types';

/**
 * Main Tree component, to be used instead of List in a Resource.
 *
 * You MUST include the tree reducer in the Admin for this component to work. Also,
 * the dataProvider must support tree methods, e.g. getTree, moveAsNthSiblingOf, etc.
 *
 * @example
 *
 * import React, { ComponentProps, FC } from 'react';
 * import { Admin, Resource, List, ListGuesser } from 'react-admin';
 * import { reducer as tree, TreeWithDetails } from '@react-admin/ra-tree';
 *
 * // tree-augmented dataProvider
 * import dataProvider from './dataProvider';
 * import i18nProvider from './i18nProvider';
 *
 * const CategoriesEdit: FC = props => (
 *     <Edit {...props}>
 *         <SimpleForm>
 *             <TextInput source="name" />
 *         </SimpleForm>
 *     </Edit>
 * );
 *
 * const CategoriesList: FC<Props> = props => (
 *     <TreeWithDetails
 *         titleField="name"
 *         edit={CategoriesEdit}
 *         draggable
 *         showLine
 *         {...props}
 *     />
 * );
 *
 * type Props = ComponentProps<typeof List>;
 *
 * export const App: FC = () => (
 *     <Admin
 *         dataProvider={dataProvider}
 *         i18nProvider={i18nProvider}
 *         customReducers={{ tree }}
 *     >
 *         <Resource name="categories" list={CategoriesList} />
 *         <Resource name="products" list={ListGuesser} />
 *     </Admin>
 * );
 */
export const TreeWithDetails = ({
    allowMultipleRoots = false,
    className,
    create: Create,
    edit: Edit,
    hideRootNodes,
    lazy = false,
    linkTo = 'edit',
    nodeActions = <DefaultNodeActions />,
    show: Show,
    showLine,
    sx,
    title,
    titleField = 'title',
    addRootButton = (
        <CreateButton
            label="ra-tree.action.add_root"
            sx={{ marginTop: '.75rem' }}
        />
    ),
    meta,
    ...props
}: TreeWithDetailsProps): ReactElement => {
    const resource = useResourceContext();
    const navigate = useNavigate();
    const createPath = useCreatePath();
    // find id in location to set it as selected
    // location can be /posts, /posts/123, or /posts/123/show, or /posts/create
    const matchCreate = useMatch(`/${resource}/create`);
    const matchEdit = useMatch(`/${resource}/:id`);
    const matchShow = useMatch(`/${resource}/:id/show`);

    const defaultSelectedKeys = useMemo(() => {
        if (matchCreate) {
            return [UNSAVED_NEW_NODE];
        }

        if (matchEdit) {
            return [matchEdit.params.id];
        }
        if (matchShow) {
            return [matchShow.params.id];
        }
    }, [matchCreate, matchEdit, matchShow]);

    const controllerProps = useTreeController({
        hideRootNodes,
        lazy,
        resource,
        titleField,
        meta,
    });

    const {
        defaultTitle,
        expandedKeys,
        handleExpand,
        isLoading,
        loadChildNodes,
        tree,
        handleDrop,
    } = controllerProps;
    const handleClick = useCallback(
        (event: MouseEvent, treeNode: EventDataNode<TreeRecord>): void => {
            navigate(createPath({ resource, id: treeNode.id, type: linkTo }));
        },
        [createPath, linkTo, navigate, resource]
    );

    const handleLoadData = useCallback(
        async (node): Promise<void> => {
            if (lazy) {
                return loadChildNodes(node.id);
            }
            return Promise.resolve();
        },
        [lazy, loadChildNodes]
    );

    if (isLoading) {
        return null;
    }

    const canCreateNode = !tree || tree.length === 0 || allowMultipleRoots;
    return (
        <TreeContextProvider value={controllerProps}>
            <Root className={className} sx={sx}>
                <div className={TreeWithDetailsClasses.cardTree}>
                    <Card>
                        <CardContent
                            className={TreeWithDetailsClasses.cardContentTree}
                        >
                            <Tree
                                treeData={tree}
                                onDrop={handleDrop}
                                onExpand={handleExpand}
                                onClick={handleClick}
                                expandedKeys={
                                    tree && Array.isArray(expandedKeys)
                                        ? expandedKeys
                                        : []
                                }
                                selectedKeys={defaultSelectedKeys}
                                showLine={showLine}
                                nodeActions={nodeActions}
                                loadData={lazy ? handleLoadData : undefined}
                                {...sanitizeRestProps(props)}
                            />
                            {canCreateNode && addRootButton}
                        </CardContent>
                    </Card>
                </div>
                <div className={TreeWithDetailsClasses.actionCard}>
                    <Routes>
                        {Create ? (
                            <Route
                                path="create/*"
                                element={
                                    isValidElement(Create) ? Create : <Create />
                                }
                            />
                        ) : null}
                        {Show ? (
                            <Route
                                path=":id/show/*"
                                element={isValidElement(Show) ? Show : <Show />}
                            />
                        ) : null}
                        {Edit ? (
                            <Route
                                path=":id/*"
                                element={isValidElement(Edit) ? Edit : <Edit />}
                            />
                        ) : null}
                        {title !== false ? (
                            <Route
                                path="*"
                                element={
                                    <Title
                                        title={title}
                                        defaultTitle={defaultTitle}
                                    />
                                }
                            />
                        ) : null}
                    </Routes>
                </div>
            </Root>
        </TreeContextProvider>
    );
};

export interface TreeWithDetailsProps extends TreeProps {
    allowMultipleRoots?: boolean;
    className?: string;
    create?: ComponentType;
    edit?: ComponentType;
    hideRootNodes?: boolean;
    lazy?: boolean;
    linkTo?: string;
    nodeActions?: ReactElement<NodeActionsProps>;
    resource?: string;
    show?: ComponentType;
    showLine?: boolean;
    sx?: SxProps;
    title?: string | ReactElement | false;
    titleField?: string;
    addRootButton?: ReactElement<CreateButtonProps> | false;
    meta?: any;
}

const sanitizeRestProps = ({
    basePath,
    history,
    location,
    match,
    options,
    hasList,
    hasEdit,
    hasShow,
    hasCreate,
    syncWithLocation,
    ...rest
}: any): any => rest;

TreeWithDetails.propTypes = {
    edit: ComponentPropType,
    create: ComponentPropType,
    resource: PropTypes.string,
};

const PREFIX = 'RaTreeWithDetails';
export const TreeWithDetailsClasses = {
    container: `${PREFIX}-container`,
    cardTree: `${PREFIX}-cardTree`,
    cardContentTree: `${PREFIX}-cardContentTree`,
    actionCard: `${PREFIX}-actionCard`,
};

const Root = styled('div', {
    name: PREFIX,
    overridesResolver: (props, styles) => styles.root,
})({
    display: 'flex',
    [`& .${TreeWithDetailsClasses.actionCard}`]: {
        width: '100%',
    },
    [`& .${TreeWithDetailsClasses.cardTree}`]: {
        minWidth: '300px',
        marginTop: '1em',
        marginRight: '1em',
    },
    [`& .${TreeWithDetailsClasses.cardContentTree}`]: {},
    [`& .${TreeWithDetailsClasses.container}`]: {
        display: 'flex',
    },
});
